/** Positions and paths for the minimap: distances, where a point lies along a road, the stretch of road
 *  between two reported positions (so the ambulance glides along the road, not across it), and a point a
 *  given share of the way along a path. Positions are [lat, lng], as Leaflet takes them. */

export type LatLng = [number, number];

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (deg: number) => (deg * Math.PI) / 180;

export function distanceM(a: LatLng, b: LatLng): number {
  const dLat = rad(b[0] - a[0]);
  const dLng = rad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Compass bearing from a to b, 0-360 (0 = north). */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const y = Math.sin(rad(b[1] - a[1])) * Math.cos(rad(b[0]));
  const x =
    Math.cos(rad(a[0])) * Math.sin(rad(b[0])) - Math.sin(rad(a[0])) * Math.cos(rad(b[0])) * Math.cos(rad(b[1] - a[1]));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Where along `path` the point closest to `p` is: segment index, share of that segment, metres away. */
function project(path: LatLng[], p: LatLng): { index: number; t: number; away: number } {
  const kx = Math.cos(rad(p[0])) * EARTH_RADIUS_M * (Math.PI / 180);
  const ky = EARTH_RADIUS_M * (Math.PI / 180);
  let best = { index: 0, t: 0, away: path.length ? distanceM(p, path[0]) : Infinity };
  for (let i = 0; i + 1 < path.length; i++) {
    const ax = (path[i][1] - p[1]) * kx;
    const ay = (path[i][0] - p[0]) * ky;
    const dx = (path[i + 1][1] - p[1]) * kx - ax;
    const dy = (path[i + 1][0] - p[0]) * ky - ay;
    const seg2 = dx * dx + dy * dy;
    const t = seg2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / seg2));
    const away = Math.hypot(ax + t * dx, ay + t * dy);
    if (away < best.away) best = { index: i, t, away };
  }
  return best;
}

/** Metres from a point to `path` (Infinity for an empty path). */
export function distanceToPath(path: LatLng[], p: LatLng): number {
  return path.length ? project(path, p).away : Infinity;
}

/** The road between two reported positions when both lie on `road` (within `toleranceM`) in driving order;
 *  else the straight line between them. */
export function stretchBetween(road: LatLng[] | null, from: LatLng, to: LatLng, toleranceM = 60): LatLng[] {
  if (!road || road.length < 2) return [from, to];
  const a = project(road, from);
  const b = project(road, to);
  if (a.away > toleranceM || b.away > toleranceM) return [from, to];
  if (b.index < a.index || (b.index === a.index && b.t < a.t)) return [from, to];
  return [from, ...road.slice(a.index + 1, b.index + 1), to];
}

/** Cumulative metres at each vertex. */
export function lengths(path: LatLng[]): number[] {
  const out = [0];
  for (let i = 1; i < path.length; i++) out.push(out[i - 1] + distanceM(path[i - 1], path[i]));
  return out;
}

/** The point `share` (0-1) of the way along `path`, the heading there, and the index of the vertex it last
 *  passed. */
export function along(
  path: LatLng[],
  cumulative: number[],
  share: number,
): { at: LatLng; heading: number | null; passed: number } {
  if (path.length === 1) return { at: path[0], heading: null, passed: 0 };
  const total = cumulative[cumulative.length - 1];
  const target = Math.max(0, Math.min(1, share)) * total;
  let i = 0;
  while (i + 2 < cumulative.length && cumulative[i + 1] < target) i++;
  const seg = cumulative[i + 1] - cumulative[i];
  const t = seg > 0 ? (target - cumulative[i]) / seg : 1;
  const a = path[i];
  const b = path[i + 1];
  return {
    at: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t],
    heading: seg > 0 ? bearingDeg(a, b) : null,
    passed: i,
  };
}
